import React, {Suspense, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {events, mediaInterface} from '@techsmith/techsmith-smartplayer';
import {TscStyles} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import {IMediaDetails} from '../../model/mediaDetails';
import {useQueryParam, StringParam} from 'use-query-params';
import Analytics from '../../service/analytics';
import config from '../../service/config';
import ScreenReaderHelper from '../util/ScreenReaderHelper';
import {PageWrapper} from '../util/StyledElements';
import PizzaTracker from './PizzaTracker';
import SmartPlayer from './SmartPlayer';
import VideoPlaceholder from './VideoPlaceholder';
import {updateReactionsFromServer} from '../../context/reactionsProvider';
import ReactionsEmojiMenu from '../reactions/ReactionsMenu';
import conversationApi from '../../service/conversationApi';
import SelfIdentifyModal from '../modals/SelfIdentifyModal';
import selfIdentifyService from '../../service/selfIdentifyservice';
import {IConversationData} from '../../model/conversationModel';
import Constants, {EmbedPlatforms} from '../../constants/Constants';
import {selfIdentifiedUserStore, updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import ReactionsImageWidget from '../reactions/ReactionsImageWidget';
import smartPlayerConstants from '../../constants/smartPlayerConstants';

// we dont use the shared-styles 'ContentArea' here because we dont need to preserve room for the header
const EmbedContentArea = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   height: 100%;
   background: ${smartPlayerConstants.videoBackgroundColor};

   /* embed view always wants this for the SP container, non-embed does _not_ so we apply weighting here intentionally */
   .smart-player-container {
      height: 100%;
      width: 100%;
      max-width: 100%;
   }
`;

const Embed: React.FC<IEmbedProps & IStateMappedProps> = ({mediaDetails, showSelfIdentifyModal}) => {
   const {t} = useTranslation();
   const [embedPlatform] = useQueryParam(Constants.queryParams.platform, StringParam);
   const [media] = useState(mediaDetails);
   const [conversationHash, setConversationHash] = useState(null);
   const [reactionsImageWidgetDomElement, setReactionsImageWidgetDomElement] = useState(null);
   const [reactionsMenuDomElement, setReactionsMenuDomElement] = useState(null);
   const [heroControlHidden, setHeroControlHidden] = useState(false);
   const [imageIsActive, setImageIsActive] = useState(true);

   const getFirstConversationHash = async (mediaHash: string) => {
      const conversations: IConversationData[] = await conversationApi.getConversations(mediaHash);
      if (conversations.length > 0) {
         setConversationHash(conversations[0].VideoReviewId);
      }
   };

   const registerSmartPlayerPlugin = async () => {
      const reactionsMenuPluginInfo = await mediaInterface.registerPlugin('techsmithReactionsMenu', Constants.pluginElements.player);
      reactionsMenuPluginInfo.pluginContainerElement.classList.add('reactions-menu-wrapper');
      setReactionsMenuDomElement(reactionsMenuPluginInfo.pluginContainerElement);

      if (mediaDetails.IsImage) {
         const reactionsImageWidgetPluginInfo = await mediaInterface.registerPlugin('techsmithReactionsImageWidget', Constants.pluginElements.player);
         reactionsImageWidgetPluginInfo.pluginContainerElement.classList.add('reactions-image-widget-wrapper');
         setReactionsImageWidgetDomElement(reactionsImageWidgetPluginInfo.pluginContainerElement);
      }
   };

   const onHeroControlHidden = () => {
      setHeroControlHidden(true);
   };

   const onImageOver = () => {
      setImageIsActive(true);
   };

   const onImageLeave = () => {
      setImageIsActive(false);
   };

   useEffect(() => {
      if (config.featureSwitches.EnableReactions && conversationHash) {
         void updateReactionsFromServer(conversationHash);
         if (!mediaDetails.IsYouTube) {
            void registerSmartPlayerPlugin();
         }
      }
   }, [conversationHash]);

   useEffect(() => {
      if (!media) {
         return;
      }

      config.user.TechSmithId === Constants.nullTechSmithId && selfIdentifyService.setupSelfIdentifiedUserFromLocalStorage();

      Analytics.initialize();

      const mediaCustomDims: Partial<IMediaDetails> = {};
      Object.keys(media).filter(key => {
         const unnecessaryKeys = ['Author', 'Description', 'Groups', 'IsDecorated'];
         return !unnecessaryKeys.includes(key);
      }).forEach(key => {
         mediaCustomDims[key] = media[key];
      });
      media && Analytics.aiConfigureCustomDimensions(mediaCustomDims, 'media');

      window.addEventListener(TscStyles.events.modalOpenedOnPage, () => mediaInterface.pause());

      if (config.featureSwitches.EnableReactions) {
         void getFirstConversationHash(media.Hash);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []); // media will never change, so ignored here, this state+effect is simply a way of initializing analytics once after entire dom (including SP) is loaded

   useEffect(() => {
      if (mediaDetails.IsImage) {
         mediaInterface.addEventListener(events.External.ImagePointerOver, onImageOver);
         mediaInterface.addEventListener(events.External.ImagePointerLeave, onImageLeave);
      } else {
         mediaInterface.addEventListener(events.ExternalVideo.VideoTimeUpdate, onHeroControlHidden);
      }
      return () => {
         if (mediaDetails.IsImage) {
            mediaInterface.removeEventListener(events.External.ImagePointerOver, onImageOver);
            mediaInterface.removeEventListener(events.External.ImagePointerLeave, onImageLeave);
         } else {
            mediaInterface.removeEventListener(events.ExternalVideo.VideoTimeUpdate, onHeroControlHidden);
         }
      };
   }, []);

   return (
      <PageWrapper>
         <ScreenReaderHelper href="#start-of-content" text={t('accessibility.skipLinkText')} />
         <EmbedContentArea className="app-content-wrapper" id="start-of-content">
            <Suspense fallback={<VideoPlaceholder />}>
               {mediaDetails.IsReady ?
                  <>
                     <SmartPlayer isEmbedded={true} embedPlatform={embedPlatform as EmbedPlatforms} />
                     {config.featureSwitches.EnableReactions && conversationHash && reactionsImageWidgetDomElement && imageIsActive && ReactDOM.createPortal(<ReactionsImageWidget isEmbedded={true}></ReactionsImageWidget>, reactionsImageWidgetDomElement as Element)}
                     {config.featureSwitches.EnableReactions && conversationHash && reactionsMenuDomElement && (heroControlHidden || (mediaDetails.IsImage && imageIsActive)) && ReactDOM.createPortal(<ReactionsEmojiMenu isEmbedded={true} conversationId={conversationHash}></ReactionsEmojiMenu>, reactionsMenuDomElement as Element)}
                  </> : <PizzaTracker isOwnerOrAdmin={config.isAdmin || config.embedConfig.isOwner}/>}
            </Suspense>
         </EmbedContentArea>
         {showSelfIdentifyModal && <SelfIdentifyModal showTosMessage={embedPlatform !== EmbedPlatforms.slack} showSignInMessage={false} isEmbedded={true} onClose={() => updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: false})} />}
      </PageWrapper>
   );
};

interface IEmbedProps {
   mediaDetails: IMediaDetails;
}

export interface IStateMappedProps {
   showSelfIdentifyModal: boolean;
}

const mapStatesToProps = (
   {mediaDetails}: IEmbedProps,
   {showSelfIdentifyModal}: Partial<IStateMappedProps>
): IStateMappedProps & IEmbedProps => ({mediaDetails, showSelfIdentifyModal});

export default withMemoizedContexts(mapStatesToProps, selfIdentifiedUserStore)(Embed);
